import { z } from "zod";
export const gymSlugSchema = z.enum(["brest", "landerneau"]);
// Date du jour (YYYY-MM-DD) au fuseau de la salle — sert de référence pour la
// règle des 48h, indépendamment du fuseau du visiteur ou du serveur.
function todayParisISODate() {
    return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris" }).format(new Date());
}
// Premier jour où une séance d'essai peut être réservée (ni le jour même, ni
// le lendemain) : J+2 par rapport à la date parisienne du jour.
function minTrialParisISODate() {
    const parts = todayParisISODate().split("-").map(Number);
    const date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    date.setUTCDate(date.getUTCDate() + 2);
    return date.toISOString().slice(0, 10);
}
export const contactSchema = z
    .object({
    // Salle concernée — obligatoire : le formulaire de contact impose un choix
    // (plus de page /contact/<salle> ni d'option « peu importe »).
    gym: gymSlugSchema,
    // prénom / nom / subject / phone finissent dans l'objet de l'e-mail de notif :
    // on écrase tout retour à la ligne / tabulation pour couper toute tentative
    // d'injection d'en-têtes, et on normalise les espaces. Le prénom sert de
    // variable {{prenom}} dans les réponses automatiques.
    firstName: z
        .string()
        .min(1)
        .max(50)
        .transform((s) => s.replace(/[\r\n\t]+/g, " ").replace(/ {2,}/g, " ").trim())
        .refine((s) => s.length > 0, "Prénom requis"),
    lastName: z
        .string()
        .min(1)
        .max(50)
        .transform((s) => s.replace(/[\r\n\t]+/g, " ").replace(/ {2,}/g, " ").trim())
        .refine((s) => s.length > 0, "Nom requis"),
    email: z.string().email(),
    phone: z
        .string()
        .max(30)
        .transform((s) => s.replace(/[\r\n\t]+/g, " ").trim())
        .optional(),
    subject: z
        .string()
        .max(150)
        .transform((s) => s.replace(/[\r\n\t]+/g, " ").trim())
        .optional(),
    message: z.string().min(10).max(5000),
    type: z.enum(["contact", "trial", "dropin"]).default("contact"),
    trialSlot: z.string().max(200).optional(),
    // Date (YYYY-MM-DD) du jour choisi dans TrialSlotPicker — permet de revalider
    // côté serveur la règle « ni le jour même, ni le lendemain », qu'un visiteur
    // pourrait contourner en modifiant la requête directement.
    trialSlotDate: z.string().date().optional(),
    // Honeypot : champ invisible côté client. On accepte n'importe quelle valeur
    // pour ne rien révéler au bot — le serveur détecte le remplissage et répond
    // un faux succès sans rien écrire (voir routes/public/contact.ts).
    website: z.string().optional(),
    // Jeton Cloudflare Turnstile (résolu côté client) — revérifié côté serveur
    // via l'API siteverify avant d'accepter le message. Absent si le CAPTCHA
    // n'est pas configuré (VITE_TURNSTILE_SITE_KEY vide), auquel cas le serveur
    // ne le réclame pas non plus (voir TURNSTILE_SECRET_KEY).
    turnstileToken: z.string().optional(),
})
    .refine((data) => !data.trialSlotDate || data.trialSlotDate >= minTrialParisISODate(), {
    message: "Le créneau d'essai doit être réservé au moins 48h à l'avance.",
    path: ["trialSlotDate"],
})
    // Une séance d'essai offerte doit toujours être rattachée à un créneau précis
    // (jour + horaire) : impossible de soumettre le formulaire sans ce choix.
    .refine((data) => data.type !== "trial" || !!data.trialSlot, {
    message: "Sélectionnez un jour et un créneau pour votre séance d'essai.",
    path: ["trialSlot"],
})
    .refine((data) => data.type !== "trial" || !!data.trialSlotDate, {
    message: "Sélectionnez un jour et un créneau pour votre séance d'essai.",
    path: ["trialSlotDate"],
});
export const loginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8).max(200),
});
// Types d'événements suivis. Les trois premiers sont historiques ; les suivants
// tracent les interactions concrètes des visiteurs (clics d'appel à l'action).
export const ANALYTICS_EVENT_TYPES = [
    "pageview",
    "reservation_click",
    "lead",
    "phone_click",
    "email_click",
    "directions_click",
    "pricing_interest",
    "reservation_open",
];
export const analyticsEventSchema = z.object({
    type: z.enum(ANALYTICS_EVENT_TYPES),
    path: z.string().max(500).optional(),
    gym: gymSlugSchema.optional(),
    meta: z.record(z.unknown()).optional(),
});
export const WEEKDAYS = [
    "Dimanche",
    "Lundi",
    "Mardi",
    "Mercredi",
    "Jeudi",
    "Vendredi",
    "Samedi",
];
// ── Réponses automatiques (auto-reply) ───────────────────────────────────────
// Textes envoyés au visiteur après l'envoi d'un formulaire. Modifiables par
// salle depuis l'admin ; si le champ est vide, ces textes par défaut sont
// utilisés. Variables disponibles : {{prenom}}, {{nom}}, {{salle}}, {{creneau}}.
export const DEFAULT_AUTOREPLY_CONTACT = `Salut {{prenom}} ! 👋

Merci pour ton message, on l'a bien reçu et on est ravis que CrossFit Héraclès t'intéresse !

Un membre de l'équipe {{salle}} revient vers toi très vite. En attendant, tu peux découvrir la box et toutes nos infos sur notre site 👉 https://www.crossfit-heracles.fr ou suivre notre quotidien et nos WODs sur Instagram 📸

À très vite,
L'équipe CrossFit Héraclès 💪`;
export const DEFAULT_AUTOREPLY_TRIAL = `Salut {{prenom}} ! 🎉

Bonne nouvelle : ta séance d'essai gratuite à {{salle}} est confirmée ✅

📅 Créneau : {{creneau}}

Pour être au top le jour J, pense à prévoir :
• une tenue de sport et une paire de baskets
• une gourde d'eau
• et surtout ta bonne humeur !

Pas besoin d'expérience : on s'occupe de tout et on t'accompagne à chaque mouvement. En attendant, viens découvrir la vie de la box sur notre site 👉 https://www.crossfit-heracles.fr ou sur Instagram 📸

On a hâte de t'accueillir !
L'équipe CrossFit Héraclès 🔥`;
/** Remplace les variables {{prenom}}, {{nom}}, {{salle}}, {{creneau}} d'un modèle. */
export function renderEmailTemplate(template, vars) {
    return template
        .replace(/\{\{\s*prenom\s*\}\}/gi, vars.prenom ?? "")
        .replace(/\{\{\s*nom\s*\}\}/gi, vars.nom ?? "")
        .replace(/\{\{\s*salle\s*\}\}/gi, vars.salle ?? "")
        .replace(/\{\{\s*creneau\s*\}\}/gi, vars.creneau ?? "");
}
